import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWohnenPlusLightFeature, PARAM_KEY_UPSELL, PARAM_VALUE_UPSELL } from './useWohnenPlusLightFeature.hook';
import { queryClient } from '@/components/Root';
import { useGetUpsellOffer } from '@/apiHooks/upsellOffer.hook';
import { OfferType, UpsellOfferOrigin } from '@/@types/upsellOffer';

/**
 * WP+ Light upsell state for the MeinPlus main app (WelcomeModal).
 *
 * The BE controls when to show the offer (e.g. after 7 days) via `showOffer`.
 * On close the upsell-offer cache is invalidated so the next fetch gets fresh data.
 */
export const useWohnenPlusLightMeinPlus = () => {
  const featureEnabled = useWohnenPlusLightFeature();
  const [searchParams, setSearchParams] = useSearchParams();
  const { upsellOffer } = useGetUpsellOffer(UpsellOfferOrigin.MEIN_PLUS, OfferType.WOHNEN_PLUS_LIGHT, featureEnabled);
  const isEligible = upsellOffer?.isEligible ?? false;
  const shouldAutoOpen = featureEnabled && isEligible && (upsellOffer?.showOffer ?? false);
  const upsellParam = searchParams.get(PARAM_KEY_UPSELL) === PARAM_VALUE_UPSELL;
  const showWohnenPlusLight = featureEnabled && isEligible && (shouldAutoOpen || upsellParam);

  useEffect(() => {
    if (shouldAutoOpen && !upsellParam) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(PARAM_KEY_UPSELL, PARAM_VALUE_UPSELL);
      setSearchParams(newSearchParams);
      queryClient.invalidateQueries({ queryKey: ['upsell-offer'] });
    }
  }, [shouldAutoOpen, upsellParam, searchParams, setSearchParams]);

  const handleClose = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['upsell-offer'] });
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(PARAM_KEY_UPSELL);
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  return { showWohnenPlusLight, onCloseWohnenPlusLight: handleClose };
};
